import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "common/components/base/modal/Modal";
import { clearInterval, setInterval } from "worker-timers";
import Button from "common/components-v2/base/button/Button";
import { ButtonType, ButtonVariant } from "common/enums/components/button.enum";
import { useAppDispatch } from "common/redux/core";
import { userActions } from "common/redux/user";
import { LogoutType } from "common/enums/logout-type.enum";
import { ENV_CONFIG } from "common/config/env.config";
import useToastify from "common/hooks/useToastify";
import labelCollection from "modules/_shared/config/langauge-keys.config";

import "./SessionTimeoutTimerModal.scss";
import { IMAGE_COLLECTION } from "assets/media/images";
import { Icon } from "common/components";

interface SessionTimeoutTimerModalProps {
  showModal: boolean;
  onCloseClick: () => void;
}

const SessionTimeoutTimerModal: FC<SessionTimeoutTimerModalProps> = ({
  showModal,
  onCloseClick
}) => {
  const { t } = useTranslation(["sessionTimeout"]);
  const { hideToast } = useToastify();

  const TIMER_TIME_OUT =
    60 * parseInt(ENV_CONFIG.USER_IDLE_AUTO_LOGOUT_TIMEOUT?.toString());
  const LAST_SECONDS_THRESHOLD = ENV_CONFIG.USER_LAST_SECONDS_THRESHOLD; // Show different UI when 30 seconds or less remain
  const [time, setTime] = useState<string>("");
  const [isLastTenSeconds, setIsLastTenSeconds] = useState<boolean>(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const dispatch = useAppDispatch();
  const timerIntervalRef: any = useRef(null);

  useEffect(() => {
    if (TIMER_TIME_OUT) {
      setTime(
        Math.floor((TIMER_TIME_OUT % 3600) / 60)
          ?.toString()
          .padStart(2, "0") +
          ":" +
          (TIMER_TIME_OUT % 60).toString().padStart(2, "0")
      );
    }
  }, [TIMER_TIME_OUT]);

  useEffect(() => {
    // hide any existing toast notifications
    hideToast();

    let timer = TIMER_TIME_OUT;
    timerIntervalRef.current = setInterval(() => {
      timer = timer - 1;

      // Track remaining seconds for last 10 seconds UI change
      setRemainingSeconds(timer);
      if (timer <= LAST_SECONDS_THRESHOLD && timer > 0) {
        setIsLastTenSeconds(true);
      }

      if (timer == 0) {
        setTime(
          Math.floor((timer % 3600) / 60)?.toString() +
            "m" +
            " " +
            (timer % 60).toString().padStart(2, "0") +
            "s"
        );
        dispatch(userActions.hasUserLoggedOut(true));
        dispatch(userActions?.userLogoutType(LogoutType.SESSION_TIMEOUT));
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      } else {
        setTime(
          Math.floor((timer % 3600) / 60)?.toString() +
            "m" +
            " " +
            (timer % 60).toString().padStart(2, "0") +
            "s"
        );
      }
    }, 1000);

    // Cleanup interval on unmount
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Clear interval when modal is hidden (e.g., via cross-tab continue message)
  useEffect(() => {
    if (!showModal && timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, [showModal]);

  const handleLogout = () => {
    dispatch(userActions.hasUserLoggedOut(true));
    dispatch(userActions?.userLogoutType(LogoutType.SESSION_TIMEOUT));
    if (timerIntervalRef?.current) {
      clearInterval(timerIntervalRef.current);
    }
  };

  const handleContinue = () => {
    if (timerIntervalRef?.current) {
      clearInterval(timerIntervalRef.current);
    }
    onCloseClick();
  };

  const getModalFooter = () => {
    // When in last 10 seconds, show different buttons
    if (isLastTenSeconds) {
      return (
        <div className="session-timeout-timer-modal__footer-buttons">
          <div className="left">
            <Button
              className="btn-close"
              type="button"
              variant={ButtonVariant.SECONDARY}
              onClick={onCloseClick}
              size="small"
            >
              {t(labelCollection.sessionClose)}
            </Button>
          </div>
          <div className="right">
            <Button
              className="btn-logout"
              type="button"
              variant={ButtonVariant.PRIMARY}
              onClick={handleLogout}
              size="small"
            >
              {t(labelCollection.logOutNow)}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="session-timeout-timer-modal__footer-buttons">
        <div className="left">
          <Button
            className="btn-continue"
            type="button"
            variant={ButtonVariant.SECONDARY}
            onClick={handleContinue}
            size="small"
          >
            {t(labelCollection.contnueWorking)}
          </Button>
        </div>
        <div className="right">
          <Button
            className="btn-logout"
            type="button"
            variant={ButtonVariant.PRIMARY}
            onClick={handleLogout}
            size="small"
          >
            {t(labelCollection.logOutNow)}
          </Button>
        </div>
      </div>
    );
  };

  const getModalBody = () => {
    // When in last 10 seconds, show different message
    if (isLastTenSeconds) {
      return (
        <div className="content-body">
          <p className="content-body__warning">
            {t(labelCollection.sessionExpired)}
          </p>
          <p className="content-body__sub-content">
            {t(labelCollection.sessionWillExpireSaveData)}
          </p>
        </div>
      );
    }

    return (
      <div className="content-body">
        <p className="content-body__sub-content">
          {t(labelCollection.timerMessage, { time: ENV_CONFIG.USER_IDLE_AUTO_LOGOUT_TIMEOUT?.toString() })}
        </p>
        {/* <p>{t(labelCollection.sessionWillExpireSaveData)}</p> */}
      </div>
    );
  };

  return (
    <Modal
      className="session-timeout-timer-modal confirmation-modal"
      show={showModal}
      headerIcon={
        <div className="header-content">
          <Icon
            src={IMAGE_COLLECTION.IC_WARNING_ICON}
            className="warning-icon"
          />
          <p className="header-content--text">
            {t(labelCollection.sessionInactivityNotice)}
          </p>
        </div>
      }
      title={""}
      footerElement={getModalFooter()}
      size="md"
      variant="alt"
      bodyClassName="session-timeout-timer-modal__body"
      footerClassName="session-timeout-timer-modal__footer"
      bodyElement={getModalBody()}
      closeModalButtonProps={{ style: { display: "none" } }} // Hide default close button
    />
  );
};

export default SessionTimeoutTimerModal;